import { TERRITORY_CENTER } from '../config';
import { entryFromComponents } from './entryFromPlace';
import type { EntryFromPlace } from './entryFromPlace';
import { loadGoogleMaps } from '../ui/maps';
import type { SuggestFailure, SuggestRow } from '../ui/suggestBox';

/**
 * The Route tab's meeting-point lookups (D122, amending D28): Google's Places library on the
 * public, domain-restricted browser key (D22), billed per session outside the D110 ledger.
 *
 * Cost guard: one explicit AutocompleteSessionToken per session, reused by every suggestion
 * request until a pick or three idle minutes. The pick goes through `placePrediction.toPlace()`,
 * which carries that session to `fetchFields` (FetchFieldsRequest has no token of its own) and
 * ends it. A Place is never built by ID here: that Details call would bill outside the session.
 * The field mask is `addressComponents` only (Place Details Essentials, the D110 server mask's tier).
 * A pick becomes D120 meeting-point input (street/unit/city/state/ZIP) for the server's Census
 * lookup; no Google coordinates, business name or clock are read or stored.
 */

export const SESSION_IDLE_MS = 180_000;
export const DETAILS_FIELDS = ['addressComponents'] as const;
const BIAS_RADIUS_M = 25_000;
const DENIED = new Set(['REQUEST_DENIED', 'PERMISSION_DENIED', 'UNAUTHENTICATED']);

export interface RoutePlaceRow extends SuggestRow {
  prediction: google.maps.places.PlacePrediction;
}

/** Only a refused key raises the banner (D28); no connection is the offline line; anything else
 *  stays on the status line, so a 3G blip never reads as "check the key restrictions". */
export const classifyPlacesError = (err: unknown): SuggestFailure['kind'] => {
  const maps = (globalThis as { google?: { maps?: Partial<typeof google.maps> } }).google?.maps;
  const Request = maps?.MapsRequestError;
  const Server = maps?.MapsServerError;
  const Network = maps?.MapsNetworkError;
  if (Request && err instanceof Request) return DENIED.has(String(err.code)) ? 'denied' : 'error';
  if (navigator.onLine === false) return 'offline';
  if (Server && err instanceof Server) return 'error';
  if (Network && err instanceof Network) return 'offline';
  return 'error';
};

/** Rows from Google's suggestions: predictions only, nullable text tolerated, five at most. */
export const toRows = (
  suggestions: readonly google.maps.places.AutocompleteSuggestion[],
  max = 5,
): RoutePlaceRow[] =>
  suggestions
    .flatMap((s) => (s.placePrediction ? [s.placePrediction] : []))
    .slice(0, max)
    .map((p) => ({
      main: p.mainText?.text || p.text.text,
      secondary: p.secondaryText?.text ?? '',
      prediction: p,
    }));

export interface RoutePlaces {
  suggest: (input: string) => Promise<RoutePlaceRow[] | SuggestFailure>;
  resolve: (row: RoutePlaceRow) => Promise<EntryFromPlace>;
}

export const createRoutePlaces = (now: () => number = Date.now): RoutePlaces => {
  let token: google.maps.places.AutocompleteSessionToken | null = null;
  let lastUse = 0;

  const session = (Token: typeof google.maps.places.AutocompleteSessionToken) => {
    if (!token || now() - lastUse >= SESSION_IDLE_MS) token = new Token();
    lastUse = now();
    return token;
  };

  return {
    suggest: async (input) => {
      try {
        await loadGoogleMaps();
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary('places');
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: session(AutocompleteSessionToken),
          includedRegionCodes: ['us'],
          locationBias: { center: TERRITORY_CENTER, radius: BIAS_RADIUS_M },
        });
        return toRows(suggestions);
      } catch (err) {
        return { kind: classifyPlacesError(err) };
      }
    },
    resolve: async (row) => {
      const place = row.prediction.toPlace();
      token = null; // fetchFields concludes the session; the next keystroke opens a new one
      await place.fetchFields({ fields: [...DETAILS_FIELDS] });
      return entryFromComponents(place.addressComponents);
    },
  };
};
